import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WhatsappForm, WhatsappFormInput } from '@neuroedge/shared';
import { apiClient, type ApiClientError } from './client';

/** WhatsApp forms (Meta "WhatsApp Flows") - built here, published on a WhatsApp number. */

const KEY = ['whatsapp-forms'] as const;

export function useWhatsappForms(enabled = true) {
  return useQuery<WhatsappForm[], ApiClientError>({
    queryKey: KEY,
    queryFn: () => apiClient.get<WhatsappForm[]>('/whatsapp-forms'),
    enabled,
  });
}

export function useCreateWhatsappForm() {
  const qc = useQueryClient();
  return useMutation<WhatsappForm, ApiClientError, WhatsappFormInput>({
    mutationFn: (input) => apiClient.post<WhatsappForm>('/whatsapp-forms', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateWhatsappForm() {
  const qc = useQueryClient();
  return useMutation<WhatsappForm, ApiClientError, { id: string; input: WhatsappFormInput }>({
    mutationFn: ({ id, input }) => apiClient.put<WhatsappForm>(`/whatsapp-forms/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePublishWhatsappForm() {
  const qc = useQueryClient();
  return useMutation<WhatsappForm, ApiClientError, string>({
    mutationFn: (id) => apiClient.post<WhatsappForm>(`/whatsapp-forms/${id}/publish`),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteWhatsappForm() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => apiClient.delete<void>(`/whatsapp-forms/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
